
import React from 'react';
import { 
  Typography, Card, CardContent, Grid, Box, Chip, Button, TextField,
  InputAdornment, ToggleButtonGroup, ToggleButton, IconButton, Snackbar, Alert
} from '@mui/material';
import { 
  Search, ViewModule, ViewList, Add, CheckCircle, Cancel, CleaningServices,
  Build, Bed, Bathtub, Wifi, Tv, AcUnit
} from '@mui/icons-material';
import ManageRoomDialog from '../components/ManageRoomDialog';

const mockRooms = [
  { number: '101', type: 'Deluxe', floor: 1, status: 'Occupied', price: '$150', capacity: 2, amenities: ['Wifi', 'TV', 'AC'] },
  { number: '102', type: 'Standard', floor: 1, status: 'Available', price: '$100', capacity: 2, amenities: ['Wifi', 'TV'] },
  { number: '201', type: 'Suite', floor: 2, status: 'Cleaning', price: '$250', capacity: 4, amenities: ['Wifi', 'TV', 'AC', 'Bathtub'] },
  { number: '202', type: 'Deluxe', floor: 2, status: 'Available', price: '$150', capacity: 2, amenities: ['Wifi', 'TV', 'AC'] },
  { number: '203', type: 'Standard', floor: 2, status: 'Maintenance', price: '$100', capacity: 2, amenities: ['Wifi', 'TV'] },
  { number: '301', type: 'Suite', floor: 3, status: 'Occupied', price: '$250', capacity: 4, amenities: ['Wifi', 'TV', 'AC', 'Bathtub'] },
  { number: '302', type: 'Deluxe', floor: 3, status: 'Available', price: '$150', capacity: 2, amenities: ['Wifi', 'TV', 'AC'] },
  { number: '303', type: 'Standard', floor: 3, status: 'Available', price: '$100', capacity: 2, amenities: ['Wifi', 'TV'] },
];

export default function Rooms() {
  const [view, setView] = React.useState('grid');
  const [rooms, setRooms] = React.useState(mockRooms);
  const [selectedRoom, setSelectedRoom] = React.useState(null);
  const [manageRoomOpen, setManageRoomOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState('edit');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const handleManageRoom = (room) => {
    setSelectedRoom(room);
    setDialogMode('edit');
    setManageRoomOpen(true);
  };

  const handleAddRoom = () => {
    setSelectedRoom(null);
    setDialogMode('add');
    setManageRoomOpen(true);
  };

  const handleSaveRoom = (roomData) => {
    if (dialogMode === 'add') {
      setRooms([...rooms, roomData]);
      setSnackbar({
        open: true,
        message: `Room ${roomData.number} added successfully!`,
        severity: 'success',
      });
    } else {
      setRooms(rooms.map(r => r.number === roomData.number ? roomData : r));
      setSnackbar({
        open: true,
        message: `Room ${roomData.number} updated successfully!`,
        severity: 'success',
      });
    }
  };

  const filteredRooms = rooms.filter((room) => {
    const query = searchQuery.toLowerCase();
    return (
      room.number.toLowerCase().includes(query) ||
      room.type.toLowerCase().includes(query) ||
      room.status.toLowerCase().includes(query)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'success';
      case 'Occupied': return 'error';
      case 'Cleaning': return 'warning';
      case 'Maintenance': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Available': return <CheckCircle />;
      case 'Occupied': return <Cancel />;
      case 'Cleaning': return <CleaningServices />;
      case 'Maintenance': return <Build />;
      default: return null;
    }
  };

  const stats = [
    { label: 'Total Rooms', value: rooms.length, color: '#1976d2' },
    { label: 'Available', value: rooms.filter(r => r.status === 'Available').length, color: '#2e7d32' },
    { label: 'Occupied', value: rooms.filter(r => r.status === 'Occupied').length, color: '#d32f2f' },
    { label: 'Cleaning', value: rooms.filter(r => r.status === 'Cleaning').length, color: '#ed6c02' },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Rooms Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and manage all hotel rooms
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddRoom}>Add Room</Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                <Typography variant="h3" fontWeight="bold" color={stat.color}>{stat.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2}>
        <TextField
          placeholder="Search rooms..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, v) => v && setView(v)}
          size="small"
        >
          <ToggleButton value="grid">
            <ViewModule />
          </ToggleButton>
          <ToggleButton value="list">
            <ViewList />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {filteredRooms.map((room) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={room.number}>
            <Card sx={{ 
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              }
            }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">{room.number}</Typography>
                    <Typography variant="body2" color="text.secondary">{room.type}</Typography>
                  </Box>
                  <Chip 
                    label={room.status} 
                    size="small"
                    color={getStatusColor(room.status)}
                    icon={getStatusIcon(room.status)}
                  />
                </Box>
                <Box mb={2}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Bed fontSize="small" color="action" />
                    <Typography variant="body2">{room.capacity} Guests</Typography>
                  </Box>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {room.price}<Typography component="span" variant="caption">/night</Typography>
                  </Typography>
                </Box>
                <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                  {room.amenities.map((amenity) => (
                    <Chip key={amenity} label={amenity} size="small" variant="outlined" />
                  ))}
                </Box>
                <Button variant="outlined" fullWidth size="small" onClick={() => handleManageRoom(room)}>
                  Manage Room
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <ManageRoomDialog
        open={manageRoomOpen}
        onClose={() => setManageRoomOpen(false)}
        room={selectedRoom}
        onSave={handleSaveRoom}
        mode={dialogMode}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
